import { useForm } from 'react-hook-form'
import { Form, useTransition } from '@remix-run/react'
import { useCallback } from 'react'
import type { getJob } from '~/models/job.server'
import type { Prisma } from '@prisma/client'
import type { HTMLInputTypeAttribute } from 'react'
import classnames from 'classnames'
import { UploadInput } from './UploadInput'
import { ExperienceFormInputWorkable } from './ExperienceFormInput.WORKABLE'
import { EducationFormInputWorkable } from './EducationFormInput.WORKABLE'
import { PhotoUploadInput } from './PhotoUploadInput'

import type { ErrorsList } from '~/types'

type FormField = {
  key: string
  type: string
  label: string
  required: boolean
  choices?: { id: string; body: string }[]
  single_answer?: boolean
  max_file_size?: number
  supported_file_types?: string[]
}

type OptionalField = {
  id: string
  type: string
  body: string
  required: boolean
  choices?: { id: string; body: string }[]
  single_answer?: boolean
  max_file_size?: number
  supported_file_types?: string[]
}

type SelectOption = {
  value: string
  label: string
}

type InputType = {
  type: HTMLInputTypeAttribute
  attrs: {
    [key: string]: string | number | boolean | SelectOption[]
  }
  customData?: {
    maxFileSize?: number
    supportedFileTypes?: string[]
  }
}

type RenderField = {
  name: string
  label: string
  type: InputType
  isCustom?: boolean
  isRequired?: boolean
}

function getInputType(field: FormField): InputType {
  const { type, key } = field

  if (type === 'string') {
    return {
      type: 'input',
      attrs: {
        type: 'text',
      },
    }
  }

  if (type === 'date') {
    return {
      type: 'input',
      attrs: {
        type: 'text',
        hint: 'YYYY-MM-DD',
      },
    }
  }

  if (type === 'multiple_choice') {
    return {
      type: 'checkboxes',
      attrs: {
        options: field.choices!.map((choice) => ({
          value: choice.id,
          label: choice.body,
        })),
        singleAnswer: field.single_answer as boolean,
      },
    }
  }

  if (type === 'numeric') {
    return {
      type: 'input',
      attrs: {
        type: 'number',
      },
    }
  }

  if (type === 'free_text') {
    return {
      type: 'textarea',
      attrs: {
        rows: 5,
      },
    }
  }

  if (type === 'file') {
    return {
      type: 'input',
      attrs: {
        type: 'file',
      },
      customData: {
        maxFileSize: field.max_file_size,
        supportedFileTypes: field.supported_file_types,
      },
    }
  }

  if (type === 'complex') {
    let type = 'none'
    if (['education', 'experience'].includes(key)) {
      type = key
    }
    return {
      type: type,
      attrs: {},
    }
  }

  if (type === 'boolean') {
    return {
      type: 'boolean',
      attrs: {
        options: [
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ],
      },
    }
  }

  if (type === 'dropdown') {
    return {
      type: 'select',
      attrs: {
        options: (field.choices || []).map((choice) => ({
          label: choice.body,
          value: choice.id,
        })),
      },
    }
  }

  if (type === 'short_text') {
    return {
      type: 'input',
      attrs: {
        type: 'text',
        hint: 'maximum 128 characters',
      },
    }
  }

  return {
    type: 'input',
    attrs: {
      type: 'text',
    },
  }
}

export const ApplicationFormField = ({
  field,
  errors,
}: {
  field: RenderField
  errors?: string[]
}) => {
  const fieldName = field.name

  if (
    field.type.attrs.type === 'file' &&
    field.label.toLowerCase() === 'photo'
  ) {
    return <PhotoUploadInput field={field} name={fieldName} errors={errors} />
  }

  if (
    field.type.attrs.type === 'file' &&
    field.label.toLowerCase() !== 'photo'
  ) {
    return <UploadInput field={field} name={fieldName} errors={errors} />
  }

  if (field.type.type === 'textarea') {
    return (
      <div className="mt-8">
        <label
          htmlFor="about"
          className="block text-sm font-medium text-gray-700"
        >
          {field.label}
          {field.isRequired && <span className="text-rose-500"> *</span>}
        </label>
        <div className="mt-1">
          <textarea
            name={fieldName}
            rows={3}
            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-gray-400 focus:ring-gray-400 sm:text-sm"
            placeholder=""
            defaultValue={''}
          />
        </div>
        {/* <p className="mt-2 text-sm text-gray-500">Max 1000 characters</p> */}
        {errors && (
          <div className="pt-1 text-red-700" id="password-error">
            {/* {errors[errors.length - 1]} */}
            {Array.isArray(errors) ? errors[errors.length - 1] : errors}
          </div>
        )}
      </div>
    )
  }

  if (field.type.type === 'none') {
    return null
  }

  if (field.type.type === 'education') {
    return (
      <>
        <div className="prose prose-lg relative mx-auto mt-24">
          <div
            className="absolute inset-0 flex items-center"
            aria-hidden="true"
          >
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-2 text-sm text-gray-500">
              Education
            </span>
          </div>
        </div>
        <EducationFormInputWorkable />
      </>
    )
  }

  if (field.type.type === 'experience') {
    return (
      <>
        <div className="prose prose-lg relative mx-auto mt-24">
          <div
            className="absolute inset-0 flex items-center"
            aria-hidden="true"
          >
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-2 text-sm text-gray-500">
              Work experience
            </span>
          </div>
        </div>
        <ExperienceFormInputWorkable />
      </>
    )
  }

  if (field.type.type === 'boolean') {
    const options = field.type.attrs.options as SelectOption[]

    return (
      <div className="mt-8">
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
          {field.isRequired && <span className="text-rose-500"> *</span>}
        </label>
        <div className="mt-1">
          <select
            name={fieldName}
            className="mt-1 block rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-accent-color focus:outline-none focus:ring-accent-color sm:text-sm"
          >
            <option value="">- select -</option>
            {options.map((option: any) => (
              <option key={option.label} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors && (
            <div className="pt-1 text-red-700" id="password-error">
              {Array.isArray(errors) ? errors[errors.length - 1] : errors}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (
    field.type.type === 'checkboxes' &&
    Array.isArray(field.type.attrs.options)
  ) {
    return (
      <div className="mt-8">
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
          {field.isRequired && <span className="text-rose-500"> *</span>}
        </label>
        {field.type.attrs.options.map((option: SelectOption, i) => (
          <div key={option.value} className="relative mt-4 flex items-start">
            <div className="flex h-5 items-center">
              <input
                id={`${fieldName}-${option.value}`}
                name={`${fieldName}`}
                value={option.value}
                type={field.type.attrs.singleAnswer ? 'radio' : 'checkbox'}
                className={classnames(
                  'h-4 w-4 border-gray-300 text-accent-color focus:ring-accent-color',
                  field.type.attrs.singleAnswer ? 'rounded-full' : 'rounded'
                )}
              />
            </div>
            <div className="ml-3 text-sm">
              <label
                htmlFor={`${fieldName}-${option.value}`}
                className="font-normal text-gray-700"
              >
                {option.label}
              </label>
            </div>
          </div>
        ))}
        {errors && (
          <div className="pt-1 text-red-700" id="password-error">
            {Array.isArray(errors) ? errors[errors.length - 1] : errors}
          </div>
        )}
      </div>
    )
  }

  if (field.type.type === 'select') {
    const options = field.type.attrs.options as SelectOption[]

    return (
      <div className="mt-8">
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
          {field.isRequired && <span className="text-rose-500"> *</span>}
        </label>
        <div className="mt-1">
          <select
            name={fieldName}
            className="mt-1 block rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-accent-color focus:outline-none focus:ring-accent-color sm:text-sm"
          >
            <option value="">- select -</option>
            {options.map((option: any) => (
              <option key={option.label} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors && (
            <div className="pt-1 text-red-700" id="password-error">
              {Array.isArray(errors) ? errors[errors.length - 1] : errors}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="mt-8">
      <label className="block text-sm font-medium text-gray-700">
        {field.label}
        {field.isRequired && <span className="text-rose-500"> *</span>}
      </label>
      <div className="mt-1">
        <input
          {...field.type.attrs}
          name={fieldName}
          id={field.name}
          autoComplete="off"
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-400 focus:ring-gray-400 sm:text-sm"
        />
        {field.type.attrs.hint && (
          <p className="mt-2 text-sm text-gray-500">
            {field.type.attrs.hint as string}
          </p>
        )}
        {errors && (
          <div className="pt-1 text-red-700" id="password-error">
            {Array.isArray(errors) ? errors[errors.length - 1] : errors}
          </div>
        )}
      </div>
    </div>
  )
}

export const ApplicationForm = ({
  job,
  errors,
}: {
  job: Awaited<ReturnType<typeof getJob>>
  errors: ErrorsList
}) => {
  const transition = useTransition()
  const fields: RenderField[] = []

  const data = job?.applicationForm?.data as Prisma.JsonObject

  const formFields = data.form_fields as FormField[]
  const optionalFields = data.questions as OptionalField[]
  const optionalFormFields = optionalFields.map((f) => ({
    key: f.id,
    label: f.body,
    type: f.type,
    required: f.required,
    choices: f.choices,
    single_answer: f.single_answer,
    max_file_size: f.max_file_size,
    supported_file_types: f.supported_file_types,
  })) as FormField[]

  fields.push({
    name: 'firstname',
    label: 'First name',
    type: { type: 'input', attrs: { type: 'text' } },
    isRequired: true,
  })

  fields.push({
    name: 'lastname',
    label: 'Last name',
    type: { type: 'input', attrs: { type: 'text' } },
    isRequired: true,
  })

  fields.push({
    name: 'email',
    label: 'Email address',
    type: { type: 'input', attrs: { type: 'email' } },
    isRequired: true,
  })

  for (let field of formFields) {
    fields.push({
      name: field.key,
      label: field.label,
      type: getInputType(field),
      isRequired: field.required,
    })
  }

  for (let field of optionalFormFields) {
    const fieldName = `answers.${field.key}`
    fields.push({
      name: fieldName,
      label: field.label,
      type: getInputType(field),
      isCustom: true,
      isRequired: field.required,
    })
  }

  return (
    <div>
      <Form method="post" encType="multipart/form-data">
        {fields.map((field, i) => (
          <ApplicationFormField
            key={i}
            field={field}
            errors={errors ? errors[field.name] : undefined}
          />
        ))}
        <div className="pt-12">
          <div className="flex justify-center">
            <button
              type="submit"
              className="inline-flex justify-center rounded-md border border-transparent bg-accent-color py-3 px-8 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
            >
              {transition.state === 'submitting'
                ? 'Submitting...'
                : 'Submit application'}
            </button>
          </div>
        </div>
      </Form>
    </div>
  )
}
